import {
    ArrowLeftIcon,
    ArrowsRightLeftIcon,
    ArrowTopRightOnSquareIcon,
    CalculatorIcon,
    ExclamationTriangleIcon,
    PencilSquareIcon,
} from "@heroicons/react/24/outline";
import {MoneyLabel} from "../../../../general/MoneyLabel.tsx";
import {toDecimalFromMoney, type Money} from "../../../../../lib/Money.ts";
import {cn, formatDate} from "../../../../../lib/utils.ts";
import type {PanelBooking} from "../types.ts";

interface JournalEntryFoldProps {
    booking: PanelBooking;
    onOpenDocument: ((documentId: string) => void) | null;
    onOpenManualBooking: ((manualBookingId: string) => void) | null;
    onOpenTransaction: ((transactionId: string) => void) | null;
    onChangeAccount: (() => void) | null;
}

function FoldAmount({amount}: {amount: Money | null}) {
    if (!amount) return null;
    return (
        <MoneyLabel value={toDecimalFromMoney(amount)} currency={amount.currency} showCurrency={true} />
    );
}

const UNKNOWN_VALUE_LABEL = "Onbekend";

function TransactionDetails({booking}: {booking: PanelBooking}) {
    return (
        <div className="mb-4 rounded-lg bg-muted/40 px-3 py-2.5">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Transactiegegevens</p>
            <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Omschrijving</dt>
                <dd className="whitespace-pre-wrap break-words text-gray-800">{booking.title}</dd>
                <dt className="text-muted-foreground">Tegenpartij</dt>
                <dd className="break-words text-gray-800">
                    {booking.relationName ?? UNKNOWN_VALUE_LABEL}
                </dd>
                <dt className="text-muted-foreground">Datum</dt>
                <dd className="text-gray-800 tabular-nums">
                    {formatDate(booking.date) || UNKNOWN_VALUE_LABEL}
                </dd>
            </dl>
        </div>
    );
}

export function JournalEntryFold({
    booking,
    onOpenDocument,
    onOpenManualBooking,
    onOpenTransaction,
    onChangeAccount,
}: JournalEntryFoldProps) {
    const isTransaction = booking.kind === "TRANSACTION";
    return (
        <tr className="border-b border-border">
            <td className="w-14" />
            <td colSpan={7} className="py-4 pl-1 pr-4">
                {isTransaction && <TransactionDetails booking={booking} />}

                <p className="mb-2 text-xs font-medium text-muted-foreground">Journaalpost</p>

                {booking.legs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        Geen journaalpost beschikbaar voor deze boeking.
                    </p>
                ) : (
                    <div className="rounded-lg bg-muted/40 px-3">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border text-xs text-muted-foreground">
                                <th className="py-1.5 pr-2 text-left font-medium">Rekening</th>
                                <th className="py-1.5 pr-2 text-left font-medium">Omschrijving</th>
                                <th className="py-1.5 pr-2 text-right font-medium">Debet</th>
                                <th className="py-1.5 text-right font-medium">Credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {booking.legs.map((leg, index) => (
                                <tr
                                    key={`${leg.accountCode}-${index}`}
                                    className={cn(
                                        "text-sm text-gray-800",
                                        leg.isCurrentAccount && "border-l-2 border-primary bg-background/60",
                                    )}
                                >
                                    <td className="py-2 pl-2 pr-2 font-mono text-xs text-gray-800">
                                        {leg.accountCode}
                                    </td>
                                    <td className="py-2 pr-2 [overflow-wrap:anywhere]">
                                        {leg.description ?? leg.accountLabel}
                                        {leg.isCurrentAccount && (
                                            <span className="ml-2 inline-flex items-center gap-1 align-middle text-xs text-primary">
                                                <ArrowLeftIcon className="size-3.5 shrink-0" aria-hidden />
                                                deze rekening
                                            </span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap py-2 pr-2 text-right tabular-nums">
                                        <FoldAmount amount={leg.debit} />
                                    </td>
                                    <td className="whitespace-nowrap py-2 pr-2 text-right tabular-nums">
                                        <FoldAmount amount={leg.credit} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}

                {!booking.legsAreBalanced && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                        <ExclamationTriangleIcon className="size-4 shrink-0" aria-hidden />
                        Deze journaalpost kan hier niet volledig getoond worden: debet en credit
                        sluiten niet op elkaar aan.
                    </p>
                )}

                <div className="mt-3 flex items-center gap-4">
                    {booking.kind === "BILL" && booking.documentId && onOpenDocument && (
                        <button
                            type="button"
                            onClick={() => onOpenDocument(booking.documentId as string)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-70"
                        >
                            <ArrowTopRightOnSquareIcon className="size-4" aria-hidden />
                            Open document
                        </button>
                    )}
                    {booking.kind === "MANUAL_BOOKING" && booking.manualBookingId && onOpenManualBooking && (
                        <button
                            type="button"
                            onClick={() => onOpenManualBooking(booking.manualBookingId as string)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-70"
                        >
                            <CalculatorIcon className="size-4" aria-hidden />
                            Open memoriaal
                        </button>
                    )}
                    {isTransaction && booking.transactionId && onOpenTransaction && (
                        <button
                            type="button"
                            onClick={() => onOpenTransaction(booking.transactionId as string)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-70"
                        >
                            <ArrowsRightLeftIcon className="size-4" aria-hidden />
                            Open transactie
                        </button>
                    )}
                    {!isTransaction && onChangeAccount && (
                        <button
                            type="button"
                            onClick={onChangeAccount}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-70"
                        >
                            <PencilSquareIcon className="size-4" aria-hidden />
                            Wijzig grootboekrekening
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}
